import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiX } from "react-icons/fi";
import leaveService from "../../services/leaveService";

const CATEGORIES = ["Teaching", "Non Teaching"];

const EMPTY_FORM = {
    name: "",
    code: "",
    allowedPerYear: 12,
    applicableCategories: ["Teaching", "Non Teaching"],
    attendanceStatus: "On Leave",
    description: "",
    isActive: true,
};

function Field({ label, required, children, error }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}
                {required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            {children}
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

export default function LeaveTypeForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    useEffect(() => {
        if (!isEdit) return;
        leaveService
            .getLeaveTypeById(id)
            .then((res) => {
                const lt = res.data?.data ?? res.data;
                setForm({
                    name: lt.name,
                    code: lt.code || "",
                    allowedPerYear: lt.allowedPerYear,
                    applicableCategories: lt.applicableCategories?.length ? lt.applicableCategories : CATEGORIES,
                    attendanceStatus: lt.attendanceStatus || "On Leave",
                    description: lt.description || "",
                    isActive: lt.isActive,
                });
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || err.message);
                navigate("/leave-types");
            })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const toggleCategory = (category) => {
        setForm((prev) => {
            const has = prev.applicableCategories.includes(category);
            const next = has
                ? prev.applicableCategories.filter((c) => c !== category)
                : [...prev.applicableCategories, category];
            return { ...prev, applicableCategories: next };
        });
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "Name is required.";
        if (form.allowedPerYear === "" || Number(form.allowedPerYear) < 0) {
            errs.allowedPerYear = "Enter a valid number of days.";
        }
        if (form.applicableCategories.length === 0) {
            errs.applicableCategories = "Select at least one category.";
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSaving(true);
        try {
            const payload = { ...form, allowedPerYear: Number(form.allowedPerYear) };
            if (isEdit) {
                await leaveService.updateLeaveType(id, payload);
                toast.success("Leave type updated successfully");
            } else {
                await leaveService.createLeaveType(payload);
                toast.success("Leave type created successfully");
            }
            navigate("/leave-types");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (fetching) return <div className="loading-center"><div className="spinner" />Loading…</div>;

    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">{isEdit ? "Edit Leave Type" : "Add Leave Type"}</h1>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/leave-types")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                        <FiSave size={14} /> {saving ? "Saving…" : "Save Leave Type"}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-header">Leave Type Details</div>
                    <div className="card-body">
                        <div className="form-grid">
                            <Field label="Name" required error={errors.name}>
                                <input
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Casual Leave"
                                    className={`form-control ${errors.name ? "error" : ""}`}
                                    autoFocus
                                />
                            </Field>

                            <Field label="Short Code">
                                <input
                                    name="code"
                                    value={form.code}
                                    onChange={handleChange}
                                    placeholder="e.g. CL"
                                    className="form-control"
                                    style={{ textTransform: "uppercase" }}
                                    maxLength={10}
                                />
                            </Field>

                            <Field label="Allowed Days / Year" required error={errors.allowedPerYear}>
                                <input
                                    type="number"
                                    name="allowedPerYear"
                                    min={0}
                                    value={form.allowedPerYear}
                                    onChange={handleChange}
                                    className={`form-control ${errors.allowedPerYear ? "error" : ""}`}
                                />
                            </Field>

                            <Field label="Maps to Attendance Status">
                                <select
                                    name="attendanceStatus"
                                    value={form.attendanceStatus}
                                    onChange={handleChange}
                                    className="form-control"
                                >
                                    <option value="On Leave">On Leave</option>
                                    <option value="Medical Leave">Medical Leave</option>
                                </select>
                                <small className="form-hint">
                                    When an application of this type is approved, employee attendance
                                    for those dates is marked with this status automatically.
                                </small>
                            </Field>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">
                                    Applicable To <span style={{ color: "var(--danger)" }}>*</span>
                                </label>
                                <div style={{ display: "flex", gap: "1rem" }}>
                                    {CATEGORIES.map((cat) => (
                                        <label key={cat} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                                            <input
                                                type="checkbox"
                                                checked={form.applicableCategories.includes(cat)}
                                                onChange={() => toggleCategory(cat)}
                                            />
                                            {cat}
                                        </label>
                                    ))}
                                </div>
                                {errors.applicableCategories && (
                                    <span className="form-error">{errors.applicableCategories}</span>
                                )}
                            </div>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Description</label>
                                <textarea
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Optional notes shown to staff when applying"
                                    className="form-control"
                                    style={{ resize: "vertical" }}
                                />
                            </div>

                            {isEdit && (
                                <Field label="Status">
                                    <select
                                        name="isActive"
                                        value={form.isActive ? "active" : "inactive"}
                                        onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.value === "active" }))}
                                        className="form-control"
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </Field>
                            )}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}